import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  PERMISSIONS,
  type ApplicationPipelineView,
  type AttentionItemView,
  type DashboardMetricCard,
  type OperationalDashboardView,
  type PermissionCode,
  type SalesOperationalExtras,
  type SupportOperationalExtras,
} from '@solar/contracts';

import type { StaffDashboardVariant } from '../../features/auth/portal-access';
import {
  collectionRatePresentation,
  displayNameFromEmail,
  formatGreeting,
  formatNairaAmount,
  humanizeStage,
  resolveAdminDeepLink,
} from '../../lib/admin-overview';
import { paths } from '../../routes/paths';

import { AdminIcon, type AdminIconName } from './AdminManagement';
import styles from './AdminRoleDashboards.module.css';

type RoleDashboardVariant = Exclude<StaffDashboardVariant, 'overview'>;

interface RoleDashboardProps {
  variant: RoleDashboardVariant;
  dashboard: OperationalDashboardView;
  userEmail?: string;
  roleLabel: string;
  can: (permission: PermissionCode) => boolean;
  navigation: ReactNode;
}

interface WorkArea {
  label: string;
  description: string;
  to: string;
  permission: PermissionCode;
  icon: AdminIconName;
}

const VARIANT_COPY: Readonly<
  Record<
    RoleDashboardVariant,
    { title: string; subtitle: string; attention: string; attentionHint: string }
  >
> = {
  sales: {
    title: 'Sales workspace',
    subtitle: 'Customer acquisition, onboarding and application journey operations.',
    attention: 'Keep customer journeys moving',
    attentionHint: 'Start with the live blockers returned for this sales view.',
  },
  finance: {
    title: 'Finance workspace',
    subtitle: 'Collections, repayment aging and payment operations for the current portfolio.',
    attention: 'Protect today’s collection flow',
    attentionHint: 'Resolve overdue, authorization and payment-attempt work first.',
  },
  support: {
    title: 'Support workspace',
    subtitle: 'Customer blockers, notification delivery and mandate support operations.',
    attention: 'Unblock customers and workflows',
    attentionHint: 'Prioritize live delivery, mandate and customer follow-up issues.',
  },
};

const WORK_AREAS: Readonly<Record<RoleDashboardVariant, readonly WorkArea[]>> = {
  sales: [
    {
      label: 'Applications',
      description: 'Review submitted applications and journey states.',
      to: paths.adminApplications,
      permission: PERMISSIONS.SUBSCRIPTIONS_READ,
      icon: 'document',
    },
    {
      label: 'Customers',
      description: 'Find profiles with onboarding work outstanding.',
      to: paths.adminCustomers,
      permission: PERMISSIONS.CUSTOMERS_READ,
      icon: 'customer',
    },
    {
      label: 'Mandates',
      description: 'Track setup after a customer accepts their contract.',
      to: paths.adminMandates,
      permission: PERMISSIONS.MANDATES_READ,
      icon: 'bank',
    },
  ],
  finance: [
    {
      label: 'Repayments',
      description: 'Review due, overdue and authorization states.',
      to: paths.adminBilling,
      permission: PERMISSIONS.BILLING_READ,
      icon: 'calendar',
    },
    {
      label: 'Payment attempts',
      description: 'Inspect failed and processing debit attempts.',
      to: paths.adminPaymentAttempts,
      permission: PERMISSIONS.PAYMENT_ATTEMPTS_READ,
      icon: 'payment',
    },
    {
      label: 'Posted payments',
      description: 'Open the authoritative posted-payment ledger.',
      to: paths.adminPayments,
      permission: PERMISSIONS.PAYMENTS_READ,
      icon: 'money',
    },
    {
      label: 'Reports',
      description: 'Generate operational finance summaries.',
      to: paths.adminReports,
      permission: PERMISSIONS.REPORTS_READ,
      icon: 'report',
    },
  ],
  support: [
    {
      label: 'Notification outbox',
      description: 'Review queued, processing and failed messages.',
      to: paths.adminNotificationOutbox,
      permission: PERMISSIONS.NOTIFICATIONS_READ,
      icon: 'document',
    },
    {
      label: 'Mandates',
      description: 'Check pending activation and provider states.',
      to: paths.adminMandates,
      permission: PERMISSIONS.MANDATES_READ,
      icon: 'bank',
    },
    {
      label: 'Cancellation requests',
      description: 'Review open customer mandate-cancellation work.',
      to: paths.adminMandateCancellations,
      permission: PERMISSIONS.MANDATES_READ,
      icon: 'warning',
    },
    {
      label: 'Customers',
      description: 'Open customer profiles for operational follow-up.',
      to: paths.adminCustomers,
      permission: PERMISSIONS.CUSTOMERS_READ,
      icon: 'customer',
    },
  ],
};

const METRIC_ICONS: Readonly<Record<string, AdminIconName>> = {
  customers_registered: 'customer',
  onboarding_incomplete: 'warning',
  applications_draft: 'document',
  applications_submitted: 'spark',
  contracts_awaiting_mandate: 'clock',
  contracts_mandate_active: 'check',
  billing_due: 'calendar',
  billing_awaiting_authorization: 'clock',
  billing_processing: 'spark',
  billing_overdue: 'warning',
  payments_failed: 'warning',
  payments_posted: 'payment',
  notifications_failed: 'warning',
  notifications_queued: 'clock',
  mandate_cancellations_open: 'document',
  mandates_pending_poll: 'bank',
};

const ATTENTION_DESCRIPTIONS: Readonly<Record<string, string>> = {
  APPLICATIONS_AWAITING_REVIEW: 'Review customer details and record the next decision.',
  MANDATES_AWAITING_ACTIVATION: 'Check the latest mandate state so the journey can continue.',
  SCHEDULES_OVERDUE: 'Follow up on instalments that have passed their due date.',
  NOTIFICATIONS_FAILED: 'Inspect failed deliveries and retry where appropriate.',
  NOTIFICATIONS_QUEUED: 'Monitor customer messages still waiting to send.',
  DISCOUNTS_NEED_REVIEW: 'Confirm the submitted discount evidence.',
  PAYMENT_ATTEMPTS_FAILED: 'Inspect the failure and determine the supported next step.',
  PAYMENT_ATTEMPTS_PROCESSING: 'Check attempts that remain in processing.',
  BILLING_AWAITING_AUTHORIZATION: 'Help customers complete payment authorization.',
  NOTIFICATIONS_STALE_PROCESSING: 'Inspect messages that have remained in processing.',
  MANDATE_CANCELLATIONS_OPEN: 'Review open customer cancellation requests.',
};

function permissionForPath(path: string | undefined): PermissionCode | undefined {
  if (path === undefined) return undefined;
  if (path.includes('customers')) return PERMISSIONS.CUSTOMERS_READ;
  if (path.includes('subscriptions')) return PERMISSIONS.SUBSCRIPTIONS_READ;
  if (path.includes('mandate-cancellation')) return PERMISSIONS.MANDATES_READ;
  if (path.includes('mandates')) return PERMISSIONS.MANDATES_READ;
  if (path.includes('payment-attempts')) return PERMISSIONS.PAYMENT_ATTEMPTS_READ;
  if (path.includes('payments')) return PERMISSIONS.PAYMENTS_READ;
  if (path.includes('billing')) return PERMISSIONS.BILLING_READ;
  if (path.includes('notifications')) return PERMISSIONS.NOTIFICATIONS_READ;
  if (path.includes('reports')) return PERMISSIONS.REPORTS_READ;
  return undefined;
}

function safeCount(value: unknown): string {
  if ((typeof value !== 'number' && typeof value !== 'string') || value === '') return '—';
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0
    ? new Intl.NumberFormat('en-NG').format(parsed)
    : '—';
}

function safeMoney(value: string | undefined): string {
  if (value === undefined || value.trim() === '' || !Number.isFinite(Number(value))) return '—';
  return formatNairaAmount(value);
}

function salesSummary(summary: OperationalDashboardView['summary']): SalesOperationalExtras | null {
  return summary !== undefined && 'customersRegistered' in summary ? summary : null;
}

function supportSummary(
  summary: OperationalDashboardView['summary'],
): SupportOperationalExtras | null {
  return summary !== undefined && 'staleProcessingNotifications' in summary ? summary : null;
}

function DashboardHeader({
  variant,
  userEmail,
  roleLabel,
  navigation,
}: Pick<RoleDashboardProps, 'variant' | 'userEmail' | 'roleLabel' | 'navigation'>) {
  const copy = VARIANT_COPY[variant];
  return (
    <header className={styles.pageHeader}>
      <div className={styles.headingBlock}>
        <p className={styles.eyebrow}>Role command centre</p>
        <h1>{copy.title}</h1>
        <p>{copy.subtitle}</p>
      </div>
      <div className={styles.headerNavigation}>{navigation}</div>
      <div className={styles.profileChip}>
        <span className={styles.avatar} aria-hidden="true">
          {displayNameFromEmail(userEmail)
            .split(' ')
            .map((part) => part[0])
            .join('')
            .slice(0, 2)}
        </span>
        <span className={styles.profileMeta}>
          <span>{formatGreeting()}</span>
          <strong>{displayNameFromEmail(userEmail)}</strong>
          <small>{roleLabel}</small>
        </span>
      </div>
    </header>
  );
}

function AttentionHero({
  variant,
  attention,
  can,
}: {
  variant: RoleDashboardVariant;
  attention: readonly AttentionItemView[];
  can: RoleDashboardProps['can'];
}) {
  const copy = VARIANT_COPY[variant];
  return (
    <section
      className={`${styles.attentionHero} ${styles[variant]}`}
      aria-labelledby="role-attention-heading"
    >
      <div className={styles.heroIntro}>
        <span className={styles.heroIcon}>
          <AdminIcon
            name={variant === 'finance' ? 'money' : variant === 'support' ? 'shield' : 'spark'}
          />
        </span>
        <div>
          <p className={styles.eyebrow}>Live attention</p>
          <h2 id="role-attention-heading">{copy.attention}</h2>
          <p>{copy.attentionHint}</p>
        </div>
      </div>
      <div className={styles.heroItems}>
        {attention.length === 0 ? (
          <div className={styles.caughtUp}>
            <AdminIcon name="check" />
            <span>
              <strong>You’re all caught up.</strong>
              <small>No immediate attention item was returned for this workspace.</small>
            </span>
          </div>
        ) : (
          attention.slice(0, 3).map((item) => {
            const permission = permissionForPath(item.route);
            const to =
              permission === undefined || can(permission)
                ? resolveAdminDeepLink(item.route, item.routeQuery)
                : undefined;
            const content = (
              <>
                <strong>{safeCount(item.count)}</strong>
                <span>{item.label}</span>
                <small>
                  {ATTENTION_DESCRIPTIONS[item.code] ?? 'Open the work area for details.'}
                </small>
              </>
            );
            return to === undefined ? (
              <article className={styles.heroItem} key={item.code}>
                {content}
              </article>
            ) : (
              <Link className={styles.heroItem} to={to} key={item.code}>
                {content}
              </Link>
            );
          })
        )}
      </div>
    </section>
  );
}

function MetricStrip({
  cards,
  can,
}: {
  cards: readonly DashboardMetricCard[];
  can: RoleDashboardProps['can'];
}) {
  return (
    <section className={styles.metricStrip} aria-label="Workspace metrics">
      {cards.map((card, index) => {
        const permission = permissionForPath(card.filterPath);
        const to =
          permission === undefined || can(permission)
            ? resolveAdminDeepLink(card.filterPath, card.filterQuery)
            : undefined;
        const content = (
          <>
            <span className={`${styles.metricIcon} ${styles[`metricTone${(index % 4) + 1}`]}`}>
              <AdminIcon name={METRIC_ICONS[card.key] ?? 'report'} />
            </span>
            <span className={styles.metricCopy}>
              <strong>{safeCount(card.value)}</strong>
              <span>{card.label}</span>
              <small>Current backend snapshot</small>
            </span>
            {to !== undefined && (
              <span className={styles.arrow}>
                <AdminIcon name="arrow" />
              </span>
            )}
          </>
        );
        return to === undefined ? (
          <article className={styles.metricCard} key={card.key}>
            {content}
          </article>
        ) : (
          <Link className={styles.metricCard} to={to} key={card.key}>
            {content}
          </Link>
        );
      })}
    </section>
  );
}

function CardHeader({ icon, title, hint }: { icon: AdminIconName; title: string; hint: string }) {
  return (
    <div className={styles.cardHeader}>
      <span className={styles.sectionIcon}>
        <AdminIcon name={icon} />
      </span>
      <div>
        <h2>{title}</h2>
        <p>{hint}</p>
      </div>
    </div>
  );
}

function PipelineCard({ pipeline }: { pipeline: ApplicationPipelineView | undefined }) {
  return (
    <section
      className={`${styles.card} ${styles.pipelineCard}`}
      aria-labelledby="sales-pipeline-heading"
    >
      <CardHeader
        icon="spark"
        title="Application journey pipeline"
        hint="Backend stages in lifecycle order"
      />
      {pipeline === undefined ? (
        <p className={styles.empty}>Pipeline data is unavailable.</p>
      ) : (
        <>
          <div className={styles.pipelineScroll}>
            <ol className={styles.pipeline} aria-labelledby="sales-pipeline-heading">
              {pipeline.stages.map((stage, index) => (
                <li key={stage.status}>
                  {index < pipeline.stages.length - 1 && (
                    <span className={styles.connector} aria-hidden="true" />
                  )}
                  <span className={styles.pipelineNode}>{index + 1}</span>
                  <strong>{safeCount(stage.count)}</strong>
                  <span>{humanizeStage(stage.status)}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className={styles.pipelineFooter}>
            <span>
              <strong>{safeCount(pipeline.openCount)}</strong> open journeys
            </span>
            <span>
              <strong>{safeCount(pipeline.awaitingReviewCount)}</strong> awaiting review
            </span>
            <span>
              <strong>{safeCount(pipeline.pipelineTotal)}</strong> in pipeline
            </span>
          </div>
        </>
      )}
    </section>
  );
}

function AttentionQueue({
  attention,
  can,
  title,
}: {
  attention: readonly AttentionItemView[];
  can: RoleDashboardProps['can'];
  title: string;
}) {
  return (
    <section className={`${styles.card} ${styles.queueCard}`}>
      <CardHeader
        icon="warning"
        title={title}
        hint="Prioritized by the backend dashboard response"
      />
      {attention.length === 0 ? (
        <p className={styles.empty}>No immediate action is required.</p>
      ) : (
        <ol className={styles.queueList}>
          {attention.map((item, index) => {
            const permission = permissionForPath(item.route);
            const to =
              permission === undefined || can(permission)
                ? resolveAdminDeepLink(item.route, item.routeQuery)
                : undefined;
            return (
              <li key={item.code}>
                <span className={styles.queueIndex}>{index + 1}</span>
                <span className={styles.queueCopy}>
                  <strong>{item.label}</strong>
                  <small>
                    {safeCount(item.count)} items ·{' '}
                    {ATTENTION_DESCRIPTIONS[item.code] ?? 'Review current work.'}
                  </small>
                </span>
                {to !== undefined && (
                  <Link to={to}>
                    Open <AdminIcon name="arrow" />
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function WorkAreas({
  variant,
  can,
}: {
  variant: RoleDashboardVariant;
  can: RoleDashboardProps['can'];
}) {
  const areas = WORK_AREAS[variant].filter((area) => can(area.permission));
  return (
    <section className={`${styles.card} ${styles.workAreaCard}`}>
      <CardHeader
        icon="shield"
        title={
          variant === 'sales'
            ? 'Journey guidance'
            : variant === 'finance'
              ? 'Finance next actions'
              : 'Support work areas'
        }
        hint="Only areas permitted for your account are shown"
      />
      {areas.length === 0 ? (
        <p className={styles.empty}>No additional work area is available.</p>
      ) : (
        <ul className={styles.workAreaList}>
          {areas.map((area) => (
            <li key={area.to}>
              <Link to={area.to}>
                <span className={styles.workAreaIcon}>
                  <AdminIcon name={area.icon} />
                </span>
                <span>
                  <strong>{area.label}</strong>
                  <small>{area.description}</small>
                </span>
                <span className={styles.arrow}>
                  <AdminIcon name="arrow" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SalesDashboard({ dashboard, can }: Pick<RoleDashboardProps, 'dashboard' | 'can'>) {
  const summary = salesSummary(dashboard.summary);
  const checkpoints =
    summary === null
      ? []
      : ([
          ['Incomplete onboarding', summary.onboardingIncomplete],
          ['Changes requested', summary.changesRequested],
          ['Approved', summary.approved],
          ['Awaiting acceptance', summary.awaitingAcceptance],
          ['Awaiting mandate', summary.awaitingMandate],
          ['Mandate active', summary.mandateActive],
        ] as const);
  return (
    <>
      <div className={styles.salesMain}>
        <PipelineCard pipeline={dashboard.pipeline} />
        <section className={`${styles.card} ${styles.checkpointCard}`}>
          <CardHeader
            icon="customer"
            title="Journey checkpoints"
            hint="Current customer and application states"
          />
          {checkpoints.length === 0 ? (
            <p className={styles.empty}>Sales summary is unavailable.</p>
          ) : (
            <dl className={styles.checkpointList}>
              {checkpoints.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{safeCount(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      </div>
      <div className={styles.lowerGrid}>
        <AttentionQueue
          attention={dashboard.attention ?? []}
          can={can}
          title="Sales attention queue"
        />
        <WorkAreas variant="sales" can={can} />
      </div>
    </>
  );
}

function FinanceDashboard({ dashboard, can }: Pick<RoleDashboardProps, 'dashboard' | 'can'>) {
  const rate = collectionRatePresentation(dashboard.financial?.collectionRate);
  const aging = dashboard.aging;
  const agingRows =
    aging === null || aging === undefined
      ? []
      : ([
          ['Due today', aging.dueToday],
          ['Upcoming this week', aging.upcomingThisWeek],
          ['Later', aging.later],
          ['Overdue', aging.overdue],
          ['Overdue 1–7 days', aging.overdueAging.days1to7],
          ['Overdue 8–30 days', aging.overdueAging.days8to30],
          ['Overdue 31+ days', aging.overdueAging.days31plus],
          ['Paid this month', aging.paidThisMonth],
        ] as const);
  return (
    <>
      <div className={styles.financeMain}>
        <section className={`${styles.card} ${styles.agingCard}`}>
          <CardHeader
            icon="calendar"
            title="Repayment aging"
            hint={
              aging === null || aging === undefined
                ? 'Permission-safe finance view'
                : `As of ${aging.asOfDate} · ${aging.timezone}`
            }
          />
          {agingRows.length === 0 ? (
            <p className={styles.empty}>Repayment aging is unavailable for this account.</p>
          ) : (
            <div className={styles.agingTableWrap}>
              <table className={styles.agingTable}>
                <thead>
                  <tr>
                    <th>Portfolio bucket</th>
                    <th>Schedules</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {agingRows.map(([label, bucket]) => (
                    <tr key={label}>
                      <th scope="row">{label}</th>
                      <td>{safeCount(bucket.count)}</td>
                      <td>{safeMoney(bucket.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className={`${styles.card} ${styles.collectionsCard}`}>
          <CardHeader
            icon="money"
            title="Collections snapshot"
            hint="Current portfolio values supplied by the backend"
          />
          {dashboard.financial === null || dashboard.financial === undefined ? (
            <p className={styles.empty}>Financial values are unavailable for this account.</p>
          ) : (
            <dl className={styles.moneyGrid}>
              <div>
                <dt>Collected</dt>
                <dd>{safeMoney(dashboard.financial.collectedAmount)}</dd>
                <small>{safeCount(dashboard.financial.collectedCount)} schedules</small>
              </div>
              <div>
                <dt>Due</dt>
                <dd>{safeMoney(dashboard.financial.dueAmount)}</dd>
                <small>{safeCount(dashboard.financial.dueCount)} schedules</small>
              </div>
              <div className={styles.overdueMoney}>
                <dt>Overdue</dt>
                <dd>{safeMoney(dashboard.financial.overdueAmount)}</dd>
                <small>{safeCount(dashboard.financial.overdueCount)} schedules</small>
              </div>
              <div>
                <dt>Collection rate</dt>
                <dd>{rate.label}</dd>
                <small>Current portfolio</small>
              </div>
            </dl>
          )}
          <div className={styles.paymentSummary}>
            <h3>Payment operations</h3>
            {dashboard.paymentSummary === null || dashboard.paymentSummary === undefined ? (
              <p className={styles.empty}>Payment outcomes are unavailable for this account.</p>
            ) : (
              <dl>
                <div>
                  <dt>Posted</dt>
                  <dd>
                    {safeCount(dashboard.paymentSummary.postedCount)} ·{' '}
                    {safeMoney(dashboard.paymentSummary.postedAmount)}
                  </dd>
                </div>
                <div>
                  <dt>Processing attempts</dt>
                  <dd>{safeCount(dashboard.paymentSummary.processingAttemptCount)}</dd>
                </div>
                <div>
                  <dt>Failed attempts</dt>
                  <dd>{safeCount(dashboard.paymentSummary.failedAttemptCount)}</dd>
                </div>
              </dl>
            )}
          </div>
        </section>
      </div>
      <div className={styles.lowerGrid}>
        <AttentionQueue
          attention={dashboard.attention ?? []}
          can={can}
          title="Payment operations queue"
        />
        <WorkAreas variant="finance" can={can} />
      </div>
    </>
  );
}

function SupportDashboard({ dashboard, can }: Pick<RoleDashboardProps, 'dashboard' | 'can'>) {
  const summary = supportSummary(dashboard.summary);
  return (
    <>
      <div className={styles.supportMain}>
        <section className={`${styles.card} ${styles.blockerCard}`}>
          <CardHeader
            icon="customer"
            title="Customer blocker overview"
            hint="Support-safe operational counts"
          />
          {summary === null ? (
            <p className={styles.empty}>Support summary is unavailable.</p>
          ) : (
            <div className={styles.blockerGrid}>
              <div>
                <span className={styles.blockerIcon}>
                  <AdminIcon name="clock" />
                </span>
                <strong>{safeCount(summary.awaitingPaymentAuthorization)}</strong>
                <span>Awaiting payment authorization</span>
              </div>
              <div>
                <span className={styles.blockerIcon}>
                  <AdminIcon name="warning" />
                </span>
                <strong>{safeCount(summary.overdueScheduleCount)}</strong>
                <span>Overdue customer follow-ups</span>
              </div>
              <div>
                <span className={styles.blockerIcon}>
                  <AdminIcon name="document" />
                </span>
                <strong>{safeCount(summary.mandateCancellationsOpen)}</strong>
                <span>Open cancellation requests</span>
              </div>
            </div>
          )}
        </section>
        <section className={`${styles.card} ${styles.healthCard}`}>
          <CardHeader
            icon="shield"
            title="Notification & mandate health"
            hint="Delivery and activation workflow state"
          />
          {summary === null ? (
            <p className={styles.empty}>Operational health is unavailable.</p>
          ) : (
            <dl className={styles.healthList}>
              <div>
                <dt>Failed notifications</dt>
                <dd className={styles.dangerValue}>{safeCount(summary.failedNotifications)}</dd>
              </div>
              <div>
                <dt>Queued notifications</dt>
                <dd>{safeCount(summary.queuedNotifications)}</dd>
              </div>
              <div>
                <dt>Stale processing</dt>
                <dd>{safeCount(summary.staleProcessingNotifications)}</dd>
              </div>
              <div>
                <dt>Mandates pending activation</dt>
                <dd>{safeCount(summary.mandatesPendingActivation)}</dd>
              </div>
            </dl>
          )}
        </section>
      </div>
      <div className={styles.lowerGrid}>
        <AttentionQueue
          attention={dashboard.attention ?? []}
          can={can}
          title="Support attention queue"
        />
        <WorkAreas variant="support" can={can} />
      </div>
    </>
  );
}

export function AdminRoleDashboard(props: RoleDashboardProps): React.JSX.Element {
  const { variant, dashboard, can } = props;
  return (
    <div className={`${styles.dashboard} ${styles[variant]}`}>
      <DashboardHeader {...props} />
      <AttentionHero variant={variant} attention={dashboard.attention ?? []} can={can} />
      <MetricStrip cards={dashboard.cards} can={can} />
      {variant === 'sales' && <SalesDashboard dashboard={dashboard} can={can} />}
      {variant === 'finance' && <FinanceDashboard dashboard={dashboard} can={can} />}
      {variant === 'support' && <SupportDashboard dashboard={dashboard} can={can} />}
      <p className={styles.updated}>Updated {new Date(dashboard.generatedAt).toLocaleString()}</p>
    </div>
  );
}
